import { useEffect, useRef, useState } from 'react';
import type {
  InspectionCapture,
  InspectionFlag,
  InspectionTour,
  InspectionTourRequest,
} from '../api/inspections';
import type { SessionFull } from '../api/missions';
import { ca } from '../i18n/ca';
import { useAuth } from './AuthContext';
import { captionFor, kindLabel, qcBadgeText, type ReviewItem } from './inspectionGroups';
import { TourPanel } from './TourPanel';
import { matchesFilter, type CaptureFilter } from './useReviewFlow';

const FLAG_ORDER: InspectionFlag[] = ['correct', 'problem', 'useless', 'repeat', 'mismatch'];

/** HH:MM for a hour like 4.5 or 22.5, the same shape the quick start's own hour step writes. */
const hourLabel = (h: number) => `${String(Math.floor(h)).padStart(2, '0')}:${h % 1 ? '30' : '00'}`;

export interface TourChapterProps {
  /** The tour's own session code, used only to build stable keys/ids across a page with several tours. */
  code: string;
  session: SessionFull | null;
  /** This tour's own captures, unmixed with any other tour's. */
  captures: InspectionCapture[] | null;
  /** This tour's own slideshow items (`buildReviewItems`), in this tour's own order. */
  items: ReviewItem[];
  autoFindings: string[];
  error?: string | null;
  /** The index, in `items`, of this tour's own currently focused capture; -1 when none of this tour's captures is. */
  gridIndex: number;
  /** Whether the full-screen viewer is open anywhere on the page — while it is, no grid card shows the "focused" outline. */
  viewerOpen: boolean;
  onOpenViewer: (localIndex: number) => void;
  onFocusIndex: (localIndex: number) => void;
  onMarkGroupCorrect: (groupKey: string) => void;
  /** The five flags and the note of a capture, on its own card: nothing is opened to mark it. */
  onToggleFlag: (uploadId: number, flag: InspectionFlag) => void;
  onNote: (uploadId: number, note: string) => void;
  /** The capture whose save has not answered yet, to say so on its card. */
  savingId?: number | null;
  /** Which captures the grid shows; a context card only with all of them. */
  filter?: CaptureFilter;
  /** The tour as a whole, and how what is decided about it is saved; absent, the panel is not drawn. */
  tour?: InspectionTour | null;
  onTour?: (body: InspectionTourRequest) => void;
}

/**
 * The note of a capture on its card: a draft of its own, saved when the box
 * is left, and replaced by what the platform holds only when that changed.
 */
function CardNote({
  capture,
  onNote,
  readOnly,
}: {
  capture: InspectionCapture;
  onNote: (uploadId: number, note: string) => void;
  readOnly: boolean;
}) {
  const t = ca.inspectionReviewPage;
  const [note, setNote] = useState(capture.note ?? '');
  // Only when what the platform holds changed, never on mounting: the effect
  // of the first render ran after a person (or a test on a busy machine) had
  // begun to type, and put the empty note back over the draft.
  const held = `${capture.uploadId}|${capture.note ?? ''}`;
  const shown = useRef(held);
  useEffect(() => {
    if (shown.current === held) return;
    shown.current = held;
    setNote(capture.note ?? '');
  }, [held, capture.note]);
  return (
    <textarea
      className={note.trim() ? 'has' : ''}
      aria-label={`${t.noteLabel}: ${capture.label ?? capture.uploadId}`}
      value={note}
      onChange={(e) => setNote(e.target.value)}
      onBlur={() => {
        if (!readOnly) onNote(capture.uploadId, note);
      }}
      readOnly={readOnly}
      placeholder={readOnly ? '' : t.cardNotePlaceholder}
      maxLength={2000}
      rows={2}
    />
  );
}

/**
 * The cards are the review (the owner, 2026-09-29): the picture, what it is
 * and what to look at, what the platform and the agent already said about
 * it, the five flags and the note, all on the card, as the review page of the
 * first artifacts had them and the platform's own did not; there, a capture
 * was marked only by opening it. The picture opens the viewer, to zoom.
 *
 * One closeup inspection tour's own mission facts and capture grid — the
 * whole of what {@code /proves/inspeccio/:code} (`InspectionReview.tsx`)
 * shows below its title, factored out so the campaign's own one-page review
 * ({@code /proves/inspeccions/campanya/:id/revisio}, `InspectionCampaignReview.tsx`)
 * can repeat it once per tour, as a chapter, without a second copy of this
 * logic. The full-screen viewer itself stays with the caller, which owns the
 * (possibly cross-tour) list of items it steps through.
 */
export function TourChapter({
  code,
  session,
  captures,
  items,
  autoFindings,
  error,
  gridIndex,
  viewerOpen,
  onOpenViewer,
  onFocusIndex,
  onMarkGroupCorrect,
  onToggleFlag,
  onNote,
  savingId = null,
  filter = 'all',
  tour = null,
  onTour,
}: TourChapterProps) {
  const t = ca.inspectionReviewPage;
  const isAdmin = useAuth().me.roles.includes('ADMIN');
  const def = session?.definition;
  const insertionMeans = def?.points?.insertion?.means;
  const extractionMeans = def?.points?.extraction?.means;
  const baseKind = def?.points?.base?.kind;
  const hour = def?.time?.hour;
  const weather = def?.time?.weather;
  // C1: TestModeFiles.daylight publishes every inspection tour at noon under a
  // clear sky on purpose, so its photos show something; the stored definition
  // above (settings.test) keeps the mission's own hour, which is what "hour"
  // already is. A reviewer seeing bright photos next to a "02:00" fact could
  // read it as a bug rather than the tour's own choice, so this says both.
  const isInspectionTour = def?.settings?.test === 'inspection';
  const missionHourDiffers = hour !== undefined && Math.abs(hour - 12) > 0.01;

  const captureItems = captures ?? [];
  const reviewedCount = captureItems.filter((c) => c.reviewedBy).length;
  // The index a card opens the viewer at is its place in the whole slideshow,
  // whatever the filter leaves on the page.
  const shown = items
    .map((item, i) => ({ item, i }))
    .filter(({ item }) =>
      item.type === 'context' ? filter === 'all' : matchesFilter(item.capture, filter),
    );
  const shownCaptures = shown.filter(({ item }) => item.type === 'capture').length;

  return (
    <>
      {def && (
        <section className="card" aria-label={t.missionFacts}>
          <h2>{def.title}</h2>
          {def.summary && <p>{def.summary}</p>}
          <div className="mission-facts-grid">
            <div>
              <h3>{t.objectives}</h3>
              {def.objectives.length === 0 ? (
                <p className="muted small">{t.noObjectives}</p>
              ) : (
                <ul>
                  {def.objectives.map((o) => (
                    <li key={o.id}>{o.title ?? o.id}</li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <h3>{t.insertion}</h3>
              <p>{insertionMeans ? (t.means[insertionMeans] ?? insertionMeans) : '—'}</p>
              <h3>{t.extraction}</h3>
              <p>{extractionMeans ? (t.means[extractionMeans] ?? extractionMeans) : '—'}</p>
            </div>
            <div>
              <h3>{t.base}</h3>
              <p>{baseKind ? (t.baseKinds[baseKind] ?? baseKind) : '—'}</p>
              <h3>{t.timeWeather}</h3>
              <p>
                {hour !== undefined ? `${Math.round(hour)}:00` : '—'}
                {weather ? ` · ${weather}` : ''}
              </p>
              {isInspectionTour && missionHourDiffers && (
                <p className="muted small">{t.photographedAtNoon(hourLabel(hour as number))}</p>
              )}
            </div>
            <div>
              <h3>{t.twists}</h3>
              {!def.twists || def.twists.length === 0 ? (
                <p className="muted small">{t.noTwists}</p>
              ) : (
                <ul>
                  {def.twists.map((tw) => (
                    <li key={tw.id}>{tw.title}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </section>
      )}
      {autoFindings.length > 0 && (
        <section className="card" aria-label={t.autoFindings}>
          <h2>{t.autoFindings}</h2>
          <ul className="plain small">
            {autoFindings.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        </section>
      )}
      {tour && onTour && <TourPanel tour={tour} onTour={onTour} />}
      <section className="card" aria-label={t.captures}>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <h2>{t.captures}</h2>
          {captureItems.length > 0 && (
            <span className="muted small">
              {t.progress(reviewedCount, captureItems.length)}
              {filter !== 'all' && ` · ${t.filterCount(shownCaptures, captureItems.length)}`}
            </span>
          )}
        </div>
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        {captures && captures.length === 0 && <p className="muted small">{t.noCaptures}</p>}
        {captureItems.length > 0 && shownCaptures === 0 && (
          <p className="muted small">{t.noneInFilter}</p>
        )}
        <div className="capture-grid review-grid">
          {shown.map(({ item, i }) =>
            item.type === 'context' ? (
              <div key={`x-${code}-${item.key}`} className="capture-card context-card">
                <button
                  type="button"
                  className="context-card-open"
                  aria-label={`${t.contextCard}: ${item.title}`}
                  onClick={() => onOpenViewer(i)}
                >
                  <span className="muted small" aria-hidden="true">
                    {t.contextCard}
                  </span>
                  <h3>{item.title}</h3>
                  <ul>
                    {item.facts.map((f, fi) => (
                      <li key={fi}>{f}</li>
                    ))}
                  </ul>
                </button>
                {isAdmin && (
                  <button
                    type="button"
                    className="small"
                    onClick={() => onMarkGroupCorrect(item.key)}
                  >
                    {t.markGroupCorrect}
                  </button>
                )}
              </div>
            ) : (
              <CaptureCard
                key={`c-${item.capture.uploadId}`}
                capture={item.capture}
                isAdmin={isAdmin}
                focused={gridIndex === i && !viewerOpen}
                saving={savingId === item.capture.uploadId}
                onOpen={() => onOpenViewer(i)}
                onFocus={() => onFocusIndex(i)}
                onToggleFlag={onToggleFlag}
                onNote={onNote}
              />
            ),
          )}
        </div>
      </section>
    </>
  );
}

/** One capture, reviewed where it stands. */
function CaptureCard({
  capture,
  isAdmin,
  focused,
  saving,
  onOpen,
  onFocus,
  onToggleFlag,
  onNote,
}: {
  capture: InspectionCapture;
  isAdmin: boolean;
  focused: boolean;
  saving: boolean;
  onOpen: () => void;
  onFocus: () => void;
  onToggleFlag: (uploadId: number, flag: InspectionFlag) => void;
  onNote: (uploadId: number, note: string) => void;
}) {
  const t = ca.inspectionReviewPage;
  const caption = captionFor(capture);
  const picture = capture.thumbUrl ?? capture.url;
  const visible = !!picture && (!capture.withheld || isAdmin);
  const mark = capture.flags.includes('problem')
    ? ' marked-problem'
    : capture.flags.includes('correct')
      ? ' marked-correct'
      : capture.flags.length > 0
        ? ' marked-other'
        : '';
  return (
    <article
      className={`capture-card review-card${focused ? ' focused' : ''}${mark}`}
      aria-label={capture.label ?? String(capture.uploadId)}
    >
      <button
        type="button"
        className="capture-open"
        aria-label={`${t.open}: ${capture.label ?? capture.uploadId}`}
        onClick={onOpen}
        onFocus={onFocus}
      >
        {visible ? (
          <img src={picture ?? undefined} alt={capture.label ?? ''} loading="lazy" />
        ) : capture.withheld ? (
          <span className="capture-withheld">{t.withheldPlayer}</span>
        ) : (
          <span className="capture-withheld">{capture.label}</span>
        )}
      </button>
      <span className="capture-label">{caption.what || capture.label}</span>
      <span className="row capture-flags">
        {capture.stopKind && <span className="chip small kind">{kindLabel(capture.stopKind)}</span>}
        {capture.kind === 'hotkey' && <span className="chip small kind">{t.hotkey}</span>}
        {capture.withheld && isAdmin && (
          <span className="chip small withheld" title={t.withheldHint}>
            {t.withheld}
          </span>
        )}
        {(capture.qcFlags ?? []).map((f) => (
          <span key={f} className="chip qc small">
            {qcBadgeText(f, capture.duplicateOfUploadId)}
          </span>
        ))}
      </span>
      {caption.why && (
        <p className="capture-hint">
          <strong>{t.whyLabel}</strong> {caption.why}
        </p>
      )}
      {(caption.look || caption.expect) && (
        <p className="capture-hint">
          <strong>{t.lookLabel}</strong> {caption.look ?? caption.expect}
        </p>
      )}
      {capture.report && capture.report.length > 0 && (
        <pre className="capture-report">{capture.report.join('\n')}</pre>
      )}
      {(capture.agentVerdict || capture.agentNote) && (
        <p className={`agent-note verdict-${capture.agentVerdict ?? 'none'}`}>
          <strong>
            {t.agentNote}
            {capture.agentVerdict
              ? ` · ${t.agentVerdicts[capture.agentVerdict] ?? capture.agentVerdict}`
              : ''}
          </strong>{' '}
          {capture.agentNote}
        </p>
      )}
      <div className="row capture-marks" role="group" aria-label={t.flags.correct}>
        {FLAG_ORDER.map((flag) => (
          <button
            key={flag}
            type="button"
            className={`chip flag-${flag} ${capture.flags.includes(flag) ? 'on' : ''}`}
            aria-pressed={capture.flags.includes(flag)}
            disabled={!isAdmin}
            onClick={() => onToggleFlag(capture.uploadId, flag)}
          >
            {t.flags[flag] ?? flag}
          </button>
        ))}
      </div>
      <CardNote capture={capture} onNote={onNote} readOnly={!isAdmin} />
      <span className="muted small capture-saved" role="status">
        {saving ? t.saving : capture.reviewedBy ? t.reviewedBy(capture.reviewedBy) : t.unreviewed}
      </span>
    </article>
  );
}
